'use client';

import type {
  EvaluationContext,
  JsonValue,
  Logger,
  ResolutionDetails,
} from '@openfeature/web-sdk';

import type { LevelTwoConfig } from '@/feature-flags/types/variant.types';

export const E2E_LEVEL_TWO_WINDOW_KEY = '__MEDPREV_E2E_LEVEL_TWO__';

export interface E2ELevelTwoSnapshot {
  config: LevelTwoConfig;
  match: boolean;
  error?: boolean;
}

declare global {
  interface Window {
    [E2E_LEVEL_TWO_WINDOW_KEY]?: E2ELevelTwoSnapshot;
  }
}

export function getE2ELevelTwoSnapshot(): E2ELevelTwoSnapshot | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window[E2E_LEVEL_TWO_WINDOW_KEY];
}

export class StaticLevelTwoProvider {
  metadata = {
    name: 'StaticLevelTwoProvider',
  };

  private snapshot: E2ELevelTwoSnapshot;

  constructor(snapshot: E2ELevelTwoSnapshot) {
    this.snapshot = snapshot;
  }

  async initialize(): Promise<void> {}

  resolveStringEvaluation(
    _flagKey: string,
    defaultValue: string,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<string> {
    if (this.snapshot.error) {
      throw new Error('StaticLevelTwoProvider: simulated evaluation error');
    }

    const hasActiveFeature = Object.values(this.snapshot.config).some(Boolean);
    const variantKey = hasActiveFeature ? 'treatment' : 'control';

    return {
      value: this.snapshot.match ? variantKey : defaultValue,
      reason: this.snapshot.match ? 'MATCH_EVALUATION_REASON' : 'NO_MATCH_EVALUATION_REASON',
      flagMetadata: {
        attachment: JSON.stringify(this.snapshot.config),
        match: this.snapshot.match,
      },
    } as ResolutionDetails<string> & {
      flagMetadata: { attachment: string; match: boolean };
    };
  }

  resolveBooleanEvaluation(
    _flagKey: string,
    defaultValue: boolean,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<boolean> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }

  resolveNumberEvaluation(
    _flagKey: string,
    defaultValue: number,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<number> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }

  resolveObjectEvaluation<T extends JsonValue>(
    _flagKey: string,
    defaultValue: T,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<T> {
    return { value: defaultValue, reason: 'DEFAULT' };
  }
}
